import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { Plan, Limit } from "../Entities/Plan";
import { Subscription } from "../Entities/Subscription";
import subscriptionService from "../Service/subscription.service";
import jobDescriptionService from "../Service/job.description.service";
import AuthService from "../Service/auth.service";
import { CurrentAccessAddon } from "../Hooks/useAccess";

const PUBLIC_PATHS = ["/sign-in", "/sign-up", "/auth/member/sign-up", "/forgot-password", "/reset-password", "/profile-screening"];

interface AccessState {
  currentPlan: Plan | null;
  currentSubscription: Subscription | null;
  currentLimits: Limit | null;
  currentMode: "base" | "credit" | "free" | null;
  currentAddons: CurrentAccessAddon[];
  changeSubscriptionMeasurements: (
    value: number,
    type: "jobPosts" | "jobApplications" | "teamMembers"
  ) => void;
}

const AccessContext = createContext<AccessState | undefined>(undefined);

export const AccessProvider = ({ children }: { children: ReactNode }) => {
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [currentLimits, setCurrentLimits] = useState<Limit | null>(null);
  const [currentMode, setCurrentMode] = useState<"base" | "credit" | "free" | null>("free");
  const [currentAddons, setCurrentAddons] = useState<CurrentAccessAddon[]>([]);

  const fetchCurrentAccess = useCallback(async () => {
    const accessResponse = await subscriptionService.getCurrentAccess();
    if (accessResponse) {
      console.log('[AccessContext] fetchCurrentAccess result:', {
        jobPostLimit: accessResponse.limits?.jobPosts,
        jobPostsProcessed: accessResponse.subscription?.measurements?.jobPosts,
        mode: accessResponse.mode,
      });
      setCurrentPlan(accessResponse.plan);
      setCurrentSubscription(accessResponse.subscription);
      setCurrentLimits(accessResponse.limits);
      setCurrentMode(accessResponse.mode);
      setCurrentAddons(accessResponse.addons);
    }
  }, []);

  const changeSubscriptionMeasurements = useCallback(
    (value: number, type: "jobPosts" | "jobApplications" | "teamMembers") => {
      setCurrentSubscription((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, measurements: { ...prev.measurements } };
        if (type === "jobPosts") updated.measurements.jobPosts += value;
        else if (type === "jobApplications") updated.measurements.applicationsProcessed += value;
        else if (type === "teamMembers") updated.measurements.teamMembers += value;
        return updated;
      });
    },
    []
  );

  useEffect(() => {
    const isPublicPath = PUBLIC_PATHS.some((p) => window.location.pathname === p);
    const hasToken = !!localStorage.getItem("access-token");

    // Fetch immediately only when already authenticated on a protected route
    if (!isPublicPath && hasToken) {
      fetchCurrentAccess();
    }

    const handleJobPostStatusChanged = () => {
      console.log('[AccessContext] JOB_POST_STATUS_CHANGED — re-fetching');
      fetchCurrentAccess();
    };

    const handleJobPostAdded = () => {
      console.log('[AccessContext] JOB_POST_ADDED — re-fetching');
      fetchCurrentAccess();
    };

    // Always listen for sign-in so we fetch access data after sign-up/sign-in navigation
    const handleSignIn = () => {
      fetchCurrentAccess();
    };

    jobDescriptionService.subscribe("JOB_POST_STATUS_CHANGED", handleJobPostStatusChanged);
    jobDescriptionService.subscribe("JOB_POST_ADDED", handleJobPostAdded);
    AuthService.getInstance().subscribe(AuthService.EVENTS.USER_SIGNED_IN, handleSignIn);

    return () => {
      jobDescriptionService.unsubscribe("JOB_POST_STATUS_CHANGED", handleJobPostStatusChanged);
      jobDescriptionService.unsubscribe("JOB_POST_ADDED", handleJobPostAdded);
      AuthService.getInstance().unsubscribe(AuthService.EVENTS.USER_SIGNED_IN, handleSignIn);
    };
  }, [fetchCurrentAccess]);

  return (
    <AccessContext.Provider
      value={{
        currentPlan,
        currentSubscription,
        currentLimits,
        currentMode,
        currentAddons,
        changeSubscriptionMeasurements,
      }}
    >
      {children}
    </AccessContext.Provider>
  );
};

export const useAccessContext = (): AccessState => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccessContext must be used within an AccessProvider");
  }
  return context;
};
